import { Component, Input, Output, EventEmitter, ChangeDetectorRef, SimpleChanges, OnChanges, OnDestroy, ViewChild, ElementRef, ViewEncapsulation } from '@angular/core';
import { merge } from 'lodash';
import { VisitorInfoModel } from 'shared/models/visitor-info/visitor-info.model';
import { TextChatsService } from 'common/text-chats/text-chats.service';
import { Subscription } from 'rxjs';
import { bufferTime } from 'rxjs/operators';
import { WorkItemTypes } from 'common/workitems/constants/workitem-types';
import { ThemeServiceHelper } from 'common/theme/service.helper';
import { ChatMessageGroupModel } from 'shared/components/chat-messages/chat-message-group.model';
import { MESSAGE_UPDATE_BUFFER_DURATION } from 'components/work-item-lane-list/work-item-lane/work-item-widget/chat-widget/chat-box/chat-box.component';
import { TextChatMessagesStoreFactoryService } from 'common/text-chat-messages-store-factory/text-chat-messages-store-factory.service';
import { MessageGroupService } from 'shared/services/message-group/message-group.service';
import { TextChatItem } from 'common/workitems/interfaces/text-chat.interface';
import { BcVisitorInfoBuilderService } from 'components/work-item-lane-list/work-item-lane/work-item-widget/shared/services/visitor-info-builder.service';

@Component({
  selector: 'app-messaging-preview',
  templateUrl: './messaging-preview.component.html',
  styleUrls: ['./messaging-preview.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class MessagingPreviewComponent implements OnChanges, OnDestroy {
  @Input()
  messagingId: string;

  @Input()
  messagingType: WorkItemTypes;

  @Input()
  tabName: string;

  @Output()
  closeAction: EventEmitter<void> = new EventEmitter();

  @ViewChild('scrollPane')
  scrollPane: ElementRef;

  public messagingThemeClass: string;
  public messagingThemeId: number;
  public visitor: VisitorInfoModel;
  public workItem: TextChatItem;
  public messages: ChatMessageGroupModel[] = [];
  public messagesLoading: boolean;

  private _isAttached: boolean;
  private _textChatInsertObservable: Subscription;
  private _textChatUpdateObservable: Subscription;
  private _messagesSubscription: Subscription;

  constructor(private _bcVisitorInfoBuilderService: BcVisitorInfoBuilderService,
              private _changeDetector: ChangeDetectorRef,
              private _messageGroupService: MessageGroupService,
              private _textChatsService: TextChatsService,
              private _textChatMessagesStoreFactoryService: TextChatMessagesStoreFactoryService) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.messagingId) {
      if (changes.messagingId.currentValue !== changes.messagingId.previousValue) {
        this.workItem = null;
        // Reload only if it's a different textChat
        this._clearSubscriptions();
        this._initMessagingItem();
        this._loadMessages();
      }
    }
  }

  ngOnDestroy(): void {
    this._clearSubscriptions();
  }

  public closeButtonClicked(): void {
    this.closeAction.emit();
  }

  public isTextChatActive(): boolean {
    return this.workItem && !this.workItem.Ended && !this.workItem.Closed && !!this.workItem.OperatorID;
  }

  public isInProgress(): boolean {
    return this.isTextChatActive() && !!this.workItem.Answered;
  }

  private _initMessagingItem(): void {
    const textChatObservable = this._textChatsService.observeTextChatByTextChatId(this.messagingId);
    this._textChatInsertObservable = (<any>textChatObservable).inserted().subscribe(textChat => {
      this.workItem = textChat;
      this._buildVisitorInfo();
      this._changeDetector.detectChanges();
    });
    this._textChatUpdateObservable = (<any>textChatObservable).updated().subscribe(textChat => {
      this.workItem = textChat;
      this._changeDetector.detectChanges();
    });

    this._getMessagingClass();
    this._getAvatarThemeId();
  }

  private _buildVisitorInfo(): void {
    this.visitor = null;

    Promise.all([
      this._bcVisitorInfoBuilderService.createVisitorDataForTextChat(this.workItem, this.messagingType),
      this._bcVisitorInfoBuilderService.getFolderName(this.workItem),
      this._bcVisitorInfoBuilderService.getDepartmentName(this.workItem)
    ]).then((result) => {
      this.visitor = merge({}, this.visitor, result[0]);
      if (this.visitor.fields) {
        this.visitor.fields.folderName.value = result[1];
        this.visitor.fields.departmentName.value = result[2];
        this.visitor.fields.customPreChatFields = [];
        this._changeDetector.detectChanges();
      }
    });
  }

  private _loadMessages(): void {
    this.messages = [];
    this.messagesLoading = true;
    this._messagesSubscription = this._textChatMessagesStoreFactoryService.create(this.messagingId).pipe(
      bufferTime(MESSAGE_UPDATE_BUFFER_DURATION)
    ).subscribe(messagesBuffer => {
      if (this.messagesLoading) {
        this.messagesLoading = false;
        this._changeDetector.detectChanges();
        this._forceScrollToBottom();
      }
      if (messagesBuffer.length) {
        this._setMessages(messagesBuffer);
        this._scrollToBottomIfMessageReceived(messagesBuffer);
      }
    });
  }

  private _setMessages(messagesBuffer): void {
    if (!messagesBuffer.length) {
      return;
    }
    this._setIsAttachedToBottom();
    this.messages = this._messageGroupService.addNewMessagesToGroups(this.messages, messagesBuffer[messagesBuffer.length - 1], this.messagingType);
    this._changeDetector.detectChanges();
  }

  private _setIsAttachedToBottom(): void {
    if (!this.scrollPane) {
      this._isAttached = true;
    } else {
      const scrollEl = this.scrollPane.nativeElement;
      this._isAttached = scrollEl.scrollTop + scrollEl.clientHeight + 1 >= scrollEl.scrollHeight;
    }
  }

  private _scrollToBottomIfMessageReceived(messagesBuffer): void {
    if (messagesBuffer.length) {
      this._scrollToBottom();
    }
  }

  private _scrollToBottom(): void {
    if (this.scrollPane && this._isAttached) {
      this.scrollPane.nativeElement.scrollTop = this.scrollPane.nativeElement.scrollHeight;
    }
  }

  private _forceScrollToBottom(): void {
    this._isAttached = true;
    this._scrollToBottom();
  }

  private _getMessagingClass(): void {
    this.messagingThemeClass = ThemeServiceHelper.getCssClass(this.messagingId);
  }

  private _getAvatarThemeId(): void {
    this.messagingThemeId = ThemeServiceHelper.getAvatarThemeId(this.messagingId);
  }

  private _clearSubscriptions(): void {
    if (this._textChatInsertObservable) {
      this._textChatInsertObservable.unsubscribe();
      this._textChatInsertObservable = null;
    }
    if (this._textChatUpdateObservable) {
      this._textChatUpdateObservable.unsubscribe();
      this._textChatUpdateObservable = null;
    }
    if (this._messagesSubscription) {
      this._messagesSubscription.unsubscribe();
      this._messagesSubscription = null;
    }
  }
}
